// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { addMonths, totalNeed, type Cents, type GoalPace, type SavePlan } from "@almanac/domain";
import { CategoryOptions } from "../components/CategoryOptions.js";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import { parseMoney } from "../lib/parse.js";
import { uuid, type Purchase } from "../lib/model.js";
import { activeSaves, byPriority, financePlan, PURCHASES_LINE, reserveAmount, savingsPlans } from "../lib/purchases.js";
import { useStore } from "../lib/store.js";

const TERMS = [12, 24, 36, 48, 60, 72, 84, 96] as const;

const dollars = (cents: Cents) => (cents / 100).toFixed(2);

export function Purchases() {
  const { store, today, announce } = useApp();
  const { plan, purchases } = useStore(store);
  const { t, money, date } = useI18n();
  const [editing, setEditing] = useState<Purchase | null>(null);
  const formHeading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  // Opening a purchase to edit (or closing it) moves focus to the form.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    formHeading.current?.focus();
  }, [editing]);
  const limit = plan.purchaseLimit ?? null;
  const line = plan.lines.find((l) => l.id === PURCHASES_LINE)?.amount ?? 0;
  const wanted = reserveAmount(purchases, limit, today);
  const plans = useMemo(() => savingsPlans(purchases, line, limit, today), [purchases, line, limit, today]);
  const saves = activeSaves(purchases);
  const financed = purchases.filter((p) => p.mode === "finance").sort(byPriority);
  const bought = purchases.filter((p) => p.mode === "save" && p.boughtOn).sort((a, b) => (a.boughtOn! < b.boughtOn! ? 1 : -1));

  const save = (p: Purchase) => {
    store.savePurchase(p, t("purchases.paymentTitle", { name: p.name }));
    announce(t("purchases.saved", { name: p.name }));
    setEditing(null);
  };

  const blank = (): Purchase => ({
    id: uuid(),
    name: "",
    price: 0,
    targetDate: addMonths(today, 12),
    mode: "save",
    priority: saves.length,
    saved: 0,
    rate: null,
    termMonths: null,
    category: null,
    eventId: null,
    boughtOn: null,
  });

  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>
        {t("purchases.intro")}
      </p>

      <SetAside wanted={wanted} line={line} need={totalNeed(saves, today)} />

      <section className="card" style={{ marginTop: 16 }} aria-labelledby="saves-title">
        <h2 id="saves-title">{t("purchases.saving")}</h2>
        {saves.length === 0 ? (
          <p className="hint">{t("purchases.noSaves")}</p>
        ) : (
          <>
            {saves.length > 1 && <p className="hint">{t("purchases.priorityHelp")}</p>}
            <ol className="purchases">
              {saves.map((p, i) => (
                <SaveItem
                  key={p.id}
                  purchase={p}
                  plan={plans.get(p.id)!}
                  first={i === 0}
                  last={i === saves.length - 1}
                  onEdit={() => setEditing(p)}
                />
              ))}
            </ol>
          </>
        )}
      </section>

      {financed.length > 0 && (
        <section className="card" style={{ marginTop: 16 }} aria-labelledby="financed-title">
          <h2 id="financed-title">{t("purchases.financed")}</h2>
          <ul className="purchases">
            {financed.map((p) => {
              const f = financePlan(p, today);
              return (
                <li key={p.id} className="purchase">
                  <h3>{p.name}</h3>
                  <p>
                    {t("purchases.financeSummary", {
                      price: money(p.price),
                      down: money(p.saved),
                      date: date(p.targetDate, "short"),
                    })}
                  </p>
                  <p>
                    <strong>{t("purchases.payment", { amount: money(f.payment), count: f.months, first: date(f.firstPayment, "short") })}</strong>
                  </p>
                  <p className="hint">
                    {t("purchases.interest", { interest: money(f.totalInterest), total: money(f.totalPaid + p.saved), last: date(f.lastPayment, "short") })}
                  </p>
                  {f.made > 0 && <p>{t("purchases.made", { made: f.made, count: f.months })}</p>}
                  <p className="hint">{t("purchases.financeBudget")}</p>
                  <div className="row">
                    <button type="button" onClick={() => setEditing(p)}>
                      {t("purchases.edit", { name: p.name })}
                    </button>
                    <DeleteButton purchase={p} />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="card" style={{ marginTop: 16 }} aria-labelledby="purchase-form-title">
        <h2 id="purchase-form-title" ref={formHeading} tabIndex={-1}>{editing && purchases.some((p) => p.id === editing.id) ? t("purchases.editTitle", { name: editing.name }) : t("purchases.add")}</h2>
        <PurchaseForm key={editing?.id ?? "new"} initial={editing ?? blank()} onSave={save} onCancel={editing ? () => setEditing(null) : undefined} />
      </section>

      {bought.length > 0 && (
        <section className="card" style={{ marginTop: 16 }} aria-labelledby="bought-title">
          <h2 id="bought-title">{t("purchases.bought")}</h2>
          <ul className="purchases">
            {bought.map((p) => (
              <li key={p.id} className="row purchase">
                <span>{t("purchases.boughtItem", { name: p.name, price: money(p.price), date: date(p.boughtOn!, "short") })}</span>
                <DeleteButton purchase={p} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** The monthly set-aside: what the goals need, or a fixed amount the user chooses. */
function SetAside({ wanted, line, need }: { wanted: Cents; line: Cents; need: Cents }) {
  const { store, announce } = useApp();
  const { plan } = useStore(store);
  const { t, money } = useI18n();
  const id = useId();
  const limit = plan.purchaseLimit ?? null;
  const [fixed, setFixed] = useState(limit === null ? dollars(need) : dollars(limit));

  return (
    <section className="card" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{t("purchases.setAside")}</h2>
      <fieldset>
        <legend className="visually-hidden">{t("purchases.setAside")}</legend>
        <div className="choices" style={{ flexDirection: "column", alignItems: "flex-start" }}>
          <label>
            <input
              type="radio"
              name={`${id}-mode`}
              checked={limit === null}
              onChange={() => {
                store.setPurchaseLimit(null);
                announce(t("purchases.autoOn"));
              }}
            />
            {t("purchases.auto", { amount: money(need) })}
          </label>
          <label>
            <input
              type="radio"
              name={`${id}-mode`}
              checked={limit !== null}
              onChange={() => store.setPurchaseLimit(parseMoney(fixed) ?? need)}
            />
            {t("purchases.fixed")}
          </label>
        </div>
      </fieldset>
      {limit !== null && (
        <div className="field" style={{ maxWidth: 240, marginTop: 8 }}>
          <label htmlFor={`${id}-amount`}>{t("purchases.fixedAmount")}</label>
          <input
            id={`${id}-amount`}
            type="text"
            inputMode="decimal"
            value={fixed}
            onChange={(e) => {
              setFixed(e.target.value);
              const cents = parseMoney(e.target.value);
              if (cents !== null) store.setPurchaseLimit(cents);
            }}
          />
        </div>
      )}
      <p style={{ marginBottom: 0 }}>
        {t("purchases.line", { amount: money(line) })}{" "}
        <a href="#/budgets">{t("purchases.openBudgets")}</a>
      </p>
      {line < wanted && (
        <p className="form-error" role="status">
          {t("purchases.noRoom", { wanted: money(wanted), fit: money(line) })}
        </p>
      )}
      {limit !== null && limit < need && <p className="hint">{t("purchases.short", { need: money(need) })}</p>}
    </section>
  );
}

export const PACE_MARK: Record<GoalPace, string> = { complete: "✓", ahead: "↑", "on-track": "•", behind: "!", overdue: "!" };

function SaveItem({ purchase: p, plan, first, last, onEdit }: { purchase: Purchase; plan: SavePlan; first: boolean; last: boolean; onEdit: () => void }) {
  const { store, today, announce } = useApp();
  const { t, money, date, percent } = useI18n();
  const id = useId();
  const [deposit, setDeposit] = useState("");
  const late = plan.projected !== null && plan.projected > p.targetDate;

  const add = (e: FormEvent) => {
    e.preventDefault();
    const cents = parseMoney(deposit);
    if (!cents) return;
    store.savePurchase({ ...p, saved: p.saved + cents }, "");
    announce(t("purchases.added", { amount: money(cents), name: p.name }));
    setDeposit("");
  };

  return (
    <li className="purchase">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>{p.name}</h3>
        <span className={`pace pace-${plan.pace}`}>
          <span aria-hidden="true">{PACE_MARK[plan.pace]}</span> {t(`purchases.pace.${plan.pace}`)}
        </span>
      </div>
      <p>{t("purchases.target", { price: money(p.price), date: date(p.targetDate, "short") })}</p>
      <label htmlFor={`${id}-progress`} className="visually-hidden">
        {t("purchases.progressLabel", { name: p.name })}
      </label>
      <progress id={`${id}-progress`} className="goal-progress" max={100} value={plan.percentComplete} />
      <p>
        {t("purchases.progress", { saved: money(p.saved), price: money(p.price), percent: percent(plan.percentComplete, 0) })}
      </p>
      {plan.pace !== "complete" && (
        <p className="hint">
          {t("purchases.monthly", { need: money(plan.need), funded: money(plan.funded) })}{" "}
          {plan.projected === null
            ? t("purchases.never")
            : late || plan.pace === "ahead"
              ? t("purchases.projected", { date: date(plan.projected, "short") })
              : ""}
        </p>
      )}
      <div className="row">
        {plan.pace !== "complete" && (
          <form className="row" onSubmit={add}>
            <label htmlFor={`${id}-deposit`} className="visually-hidden">
              {t("purchases.addSavedLabel", { name: p.name })}
            </label>
            <input
              id={`${id}-deposit`}
              type="text"
              inputMode="decimal"
              size={9}
              placeholder={dollars(plan.funded || plan.need)}
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
            />
            <button type="submit" disabled={!parseMoney(deposit)}>
              {t("purchases.addSaved")}
            </button>
          </form>
        )}
        <button type="button" onClick={onEdit} aria-label={t("purchases.edit", { name: p.name })}>
          {t("purchases.editShort")}
        </button>
        {!first && (
          <button type="button" onClick={() => store.movePurchase(p.id, -1)} aria-label={t("purchases.moveUp", { name: p.name })}>
            ↑ {t("purchases.moveUpShort")}
          </button>
        )}
        {!last && (
          <button type="button" onClick={() => store.movePurchase(p.id, 1)} aria-label={t("purchases.moveDown", { name: p.name })}>
            ↓ {t("purchases.moveDownShort")}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            store.savePurchase({ ...p, boughtOn: today }, "");
            announce(t("purchases.markedBought", { name: p.name }));
          }}
        >
          {t("purchases.markBought")}
        </button>
        <DeleteButton purchase={p} />
      </div>
    </li>
  );
}

function DeleteButton({ purchase }: { purchase: Purchase }) {
  const { store, announce } = useApp();
  const { t } = useI18n();
  return (
    <button
      type="button"
      className="link-button"
      onClick={() => {
        if (!confirm(t(purchase.eventId ? "purchases.confirmDeleteLoan" : "purchases.confirmDelete", { name: purchase.name }))) return;
        store.deletePurchase(purchase.id);
        announce(t("purchases.deleted", { name: purchase.name }));
      }}
    >
      {t("purchases.delete", { name: purchase.name })}
    </button>
  );
}

function PurchaseForm({ initial, onSave, onCancel }: { initial: Purchase; onSave: (p: Purchase) => void; onCancel?: () => void }) {
  const { today } = useApp();
  const { t, money } = useI18n();
  const id = useId();
  const [name, setName] = useState(initial.name);
  const [price, setPrice] = useState(initial.price ? dollars(initial.price) : "");
  const [target, setTarget] = useState(initial.targetDate);
  const [mode, setMode] = useState(initial.mode);
  const [saved, setSaved] = useState(initial.saved ? dollars(initial.saved) : "");
  const [rate, setRate] = useState(initial.rate === null ? "" : String(Math.round(initial.rate * 10000) / 100));
  const [term, setTerm] = useState(initial.termMonths ?? 60);
  const [cat, setCat] = useState(initial.category ?? "");
  const [error, setError] = useState<string | null>(null);

  const draft = (): Purchase | string => {
    const cents = parseMoney(price);
    const have = saved.trim() === "" ? 0 : parseMoney(saved);
    const pct = rate.trim() === "" ? 0 : Number(rate.replace(",", ".").replace("%", ""));
    if (!name.trim()) return t("purchases.err.name");
    if (!cents) return t("purchases.err.price");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) return t("purchases.err.date");
    if (have === null) return t("purchases.err.saved");
    if (mode === "finance" && (!Number.isFinite(pct) || pct < 0 || pct > 40)) return t("purchases.err.rate");
    return {
      ...initial,
      name: name.trim().slice(0, 80),
      price: cents,
      targetDate: target,
      mode,
      saved: have,
      rate: mode === "finance" ? pct / 100 : null,
      termMonths: mode === "finance" ? term : null,
      category: mode === "finance" ? cat || null : null,
    };
  };

  const preview = (() => {
    const d = draft();
    if (typeof d === "string") return null;
    if (d.mode === "finance") {
      const f = financePlan(d, today);
      return t("purchases.previewFinance", { amount: money(f.payment), count: f.months, interest: money(f.totalInterest) });
    }
    const need = totalNeed([d], today);
    return need > 0 ? t("purchases.previewSave", { amount: money(need) }) : null;
  })();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const d = draft();
    if (typeof d === "string") return setError(d);
    setError(null);
    onSave(d);
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${id}-name`}>{t("purchases.name")}</label>
          <input id={`${id}-name`} type="text" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor={`${id}-price`}>{t("purchases.price")}</label>
          <input id={`${id}-price`} type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor={`${id}-date`}>{t("purchases.date")}</label>
          <input id={`${id}-date`} type="date" value={target} onChange={(e) => setTarget(e.target.value)} required />
        </div>
      </div>
      <fieldset style={{ marginBottom: 12 }}>
        <legend>{t("purchases.mode")}</legend>
        <div className="choices">
          {(["save", "finance"] as const).map((m) => (
            <label key={m}>
              <input type="radio" name={`${id}-mode`} checked={mode === m} onChange={() => setMode(m)} />
              {t(`purchases.mode.${m}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${id}-saved`}>{t(mode === "finance" ? "purchases.down" : "purchases.savedSoFar")}</label>
          <input id={`${id}-saved`} type="text" inputMode="decimal" value={saved} onChange={(e) => setSaved(e.target.value)} />
        </div>
        {mode === "finance" && (
          <>
            <div className="field">
              <label htmlFor={`${id}-rate`}>{t("purchases.rate")}</label>
              <input id={`${id}-rate`} type="text" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor={`${id}-term`}>{t("purchases.term")}</label>
              <select id={`${id}-term`} value={term} onChange={(e) => setTerm(Number(e.target.value))}>
                {TERMS.map((m) => (
                  <option key={m} value={m}>
                    {t("purchases.termMonths", { count: m })}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor={`${id}-category`}>{t("purchases.category")}</label>
              <select id={`${id}-category`} value={cat} onChange={(e) => setCat(e.target.value)} aria-describedby={`${id}-category-hint`}>
                <option value="">{t("events.noCategory")}</option>
                <CategoryOptions withIncome={false} />
              </select>
              <p className="hint" id={`${id}-category-hint`}>
                {t("purchases.categoryHint")}
              </p>
            </div>
          </>
        )}
      </div>
      {preview && (
        <p aria-live="polite">
          <strong>{preview}</strong>
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button type="submit" className="primary">
          {onCancel ? t("common.save") : t("purchases.addButton")}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}
